/**
 * Kunnie wishlist — stored per device in localStorage (product handles).
 *
 * <kunnie-wishlist-button data-handle data-label-add data-label-remove>
 *   wraps a <button>; toggles the product and reflects state via aria-pressed.
 * <kunnie-wishlist-count> shows the number of saved products (hidden when 0).
 * <kunnie-wishlist-page data-section [data-limit]> renders saved products as Kunnie
 *   cards by requesting each product URL with ?section_id=<data-section>.
 *
 * All instances stay in sync through the `kunnie:wishlist-change` event and the
 * cross-tab `storage` event. Storage can be unavailable (private mode, blocked
 * site data); the wishlist then simply stays empty.
 *
 * Account sync: when a customer is logged in (config in #kunnie-config, rendered
 * by layout/theme.liquid), the list is mirrored to their account through the app
 * proxy /apps/kunnie/wishlist (Supabase function kunnie-storefront). On the first
 * sync of a device the guest list is merged into the account; afterwards the
 * account wins unless this device has unsent changes. Logging out clears the
 * previous customer's list from the device — it stays safe in their account.
 */

const STORAGE_KEY = 'kunnie:wishlist';
const OWNER_KEY = 'kunnie:wishlist-owner';
const DIRTY_KEY = 'kunnie:wishlist-dirty';
const CHANGE_EVENT = 'kunnie:wishlist-change';
const PUSH_DELAY = 400;

const storage = {
  /** @param {string} key */
  get(key) {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  /** @param {string} key @param {string} value */
  set(key, value) {
    try {
      localStorage.setItem(key, value);
    } catch {
      // Storage unavailable: keep working for this page view only.
    }
  },
  /** @param {string} key */
  remove(key) {
    try {
      localStorage.removeItem(key);
    } catch {
      // ignore
    }
  },
};

/** @returns {{ customerId: string | null, endpoint: string } | null} */
function readAccountConfig() {
  try {
    const config = JSON.parse(document.getElementById('kunnie-config')?.textContent || '{}');
    if (!config.wishlistEndpoint) return null;
    return { customerId: config.customerId ? String(config.customerId) : null, endpoint: config.wishlistEndpoint };
  } catch {
    return null;
  }
}

const account = readAccountConfig();

/** @returns {string[]} */
function readList() {
  try {
    const list = JSON.parse(storage.get(STORAGE_KEY) || '[]');
    return Array.isArray(list) ? list.filter((item) => typeof item === 'string') : [];
  } catch {
    return [];
  }
}

/**
 * @param {string[]} list
 * @param {{ sync?: boolean }} [options] - sync: false when the change came from the account itself
 */
function writeList(list, { sync = true } = {}) {
  storage.set(STORAGE_KEY, JSON.stringify(list));
  document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { list } }));
  if (sync) schedulePush();
}

// ---------- Account sync ----------

/** @type {ReturnType<typeof setTimeout> | undefined} */
let pushTimer;

/**
 * @param {'GET' | 'POST'} method
 * @param {object} [body]
 * @returns {Promise<{ loggedIn: boolean, handles: string[] }>}
 */
async function requestAccount(method, body) {
  if (!account) throw new Error('wishlist account sync disabled');
  const response = await fetch(account.endpoint, {
    method,
    credentials: 'same-origin',
    headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
    body: body ? JSON.stringify(body) : undefined,
  });
  // Shopify answers proxy failures with an HTML page, so only trust JSON.
  if (!response.ok || !(response.headers.get('content-type') || '').includes('application/json')) {
    throw new Error(`wishlist sync failed (${response.status})`);
  }
  return response.json();
}

function schedulePush() {
  if (!account?.customerId) return;
  storage.set(DIRTY_KEY, '1');
  clearTimeout(pushTimer);
  pushTimer = setTimeout(pushToAccount, PUSH_DELAY);
}

async function pushToAccount() {
  try {
    const result = await requestAccount('POST', { handles: readList() });
    if (result.loggedIn) storage.remove(DIRTY_KEY);
  } catch {
    // Stays marked dirty and is retried on the next change or page load.
  }
}

async function syncWithAccount() {
  if (!account) return;
  const owner = storage.get(OWNER_KEY);

  if (!account.customerId) {
    if (owner) {
      storage.remove(OWNER_KEY);
      storage.remove(DIRTY_KEY);
      writeList([], { sync: false });
    }
    return;
  }

  try {
    const remote = await requestAccount('GET');
    if (!remote.loggedIn) return;
    const local = readList();
    const dirty = owner === account.customerId && Boolean(storage.get(DIRTY_KEY));
    const next =
      owner === account.customerId
        ? dirty
          ? local
          : remote.handles
        : [...new Set([...local, ...remote.handles])];

    storage.set(OWNER_KEY, account.customerId);
    writeList(next, { sync: false });
    if (next.join() !== remote.handles.join()) await pushToAccount();
    else storage.remove(DIRTY_KEY);
  } catch {
    // Offline or proxy unavailable: the device list keeps working.
  }
}

/** @param {string} handle */
function toggle(handle) {
  const list = readList();
  const next = list.includes(handle) ? list.filter((item) => item !== handle) : [handle, ...list];
  writeList(next);
  document.dispatchEvent(new CustomEvent('kunnie:track', { detail: { event: next.includes(handle) ? 'wishlist_item_added' : 'wishlist_item_removed', properties: { product_handle: handle, items_count: next.length } } }));
  return next.includes(handle);
}

/** Subscribes to wishlist changes in this tab and others until `signal` aborts. */
function subscribe(callback, signal) {
  document.addEventListener(CHANGE_EVENT, callback, { signal });
  window.addEventListener(
    'storage',
    (event) => {
      if (event.key === STORAGE_KEY) callback();
    },
    { signal }
  );
}

class KunnieWishlistButton extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.querySelector('button')?.addEventListener('click', this.#onClick, { signal });
    subscribe(() => this.#render(), signal);
    this.#render();
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    event.preventDefault();
    const handle = this.dataset.handle;
    if (handle) toggle(handle);
  };

  #render() {
    const button = this.querySelector('button');
    if (!button || !this.dataset.handle) return;
    const saved = readList().includes(this.dataset.handle);
    button.setAttribute('aria-pressed', String(saved));
    const label = saved ? this.dataset.labelRemove : this.dataset.labelAdd;
    if (label) button.setAttribute('aria-label', label);
  }
}

class KunnieWishlistCount extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;

  connectedCallback() {
    this.#controller = new AbortController();
    subscribe(() => this.#render(), this.#controller.signal);
    this.#render();
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  #render() {
    const count = readList().length;
    this.textContent = count ? String(count) : '';
    this.hidden = count === 0;
  }
}

class KunnieWishlistPage extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;
  /** @type {AbortController | undefined} */
  #request;

  connectedCallback() {
    this.#controller = new AbortController();
    subscribe(() => this.#render(), this.#controller.signal);
    this.#render();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#request?.abort();
  }

  async #render() {
    const grid = this.querySelector('[data-grid]');
    const empty = this.querySelector('[data-empty]');
    if (!grid || !empty) return;

    const limit = Number(this.dataset.limit) || Infinity;
    const list = readList().slice(0, limit);
    empty.hidden = list.length > 0;
    grid.hidden = list.length === 0;
    if (!list.length) {
      grid.replaceChildren();
      return;
    }

    this.#request?.abort();
    this.#request = new AbortController();
    const { signal } = this.#request;
    const root = window.Shopify?.routes?.root || '/';
    const section = this.dataset.section || 'kunnie-card-render';

    const cards = await Promise.all(
      list.map(async (handle) => {
        try {
          const response = await fetch(`${root}products/${encodeURIComponent(handle)}?section_id=${section}`, { signal });
          if (!response.ok) return null;
          const html = await response.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const card = doc.querySelector('.kunnie-card');
          if (!card) return null;
          const item = document.createElement('li');
          item.className = 'kunnie-wishlist__item';
          item.append(document.importNode(card, true));
          return item;
        } catch {
          return null;
        }
      })
    );
    if (signal.aborted) return;

    // Drop products that no longer exist, so the count stays honest.
    const missing = list.filter((_, index) => !cards[index]);
    if (missing.length) writeList(readList().filter((handle) => !missing.includes(handle)));

    grid.replaceChildren(.../** @type {HTMLLIElement[]} */ (cards.filter(Boolean)));
    empty.hidden = grid.children.length > 0;
    grid.hidden = grid.children.length === 0;
  }
}

syncWithAccount();

if (!customElements.get('kunnie-wishlist-button')) customElements.define('kunnie-wishlist-button', KunnieWishlistButton);
if (!customElements.get('kunnie-wishlist-count')) customElements.define('kunnie-wishlist-count', KunnieWishlistCount);
if (!customElements.get('kunnie-wishlist-page')) customElements.define('kunnie-wishlist-page', KunnieWishlistPage);
